import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Crown, Star, Gift, Calendar, CreditCard, TrendingUp, ArrowRight, CheckCircle2, Lock, XCircle, AlertTriangle } from 'lucide-react';

// Cancellation Modal Component
const CancellationModal = ({ 
  currentTier, 
  periodEnd, 
  cancelConfirmText, 
  setCancelConfirmText, 
  cancelLoading, 
  onCancel, 
  onClose,
  formatDate 
}) => {
  const isFree = currentTier === 'free';
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {isFree ? 'Cancel Account?' : 'Cancel Subscription?'}
            </h3>
            <p className="text-sm text-gray-500">This action cannot be undone</p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <h4 className="font-semibold text-amber-800 mb-2">
            {isFree ? 'You will lose access to:' : 'You will lose access to:'}
          </h4>
          <ul className="space-y-2 text-sm text-amber-700">
            {isFree ? (
              <>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Community features
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Public profile
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Explore & connect with others
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Treaty education resources
                </li>
              </>
            ) : (
              <>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  AI Ancestry Analysis
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Unlimited Crest Generator
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  The Guru AI Guide
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Healing & Herbal Remedies
                </li>
                <li className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Language Restoration Tools
                </li>
                {currentTier === 'crown' && (
                  <>
                    <li className="flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Official Genealogy Paperwork
                    </li>
                    <li className="flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Private ID Application
                    </li>
                  </>
                )}
              </>
            )}
          </ul>
        </div>

        <div className="mb-6">
          {!isFree && periodEnd && (
            <p className="text-sm text-gray-600 mb-2">
              Your access will continue until <strong>{formatDate(periodEnd)}</strong>
            </p>
          )}
          <p className="text-sm text-gray-500 mb-4">
            Type <strong>&quot;cancel&quot;</strong> to confirm:
          </p>
          <input
            type="text"
            value={cancelConfirmText}
            onChange={(e) => setCancelConfirmText(e.target.value)}
            placeholder="Type cancel to confirm"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-0 focus:outline-none"
            data-testid="cancel-confirm-input"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            data-testid="cancel-modal-back-btn"
          >
            {isFree ? 'Keep Account' : 'Keep Subscription'}
          </button>
          <button
            onClick={onCancel}
            disabled={cancelConfirmText.toLowerCase() !== 'cancel' || cancelLoading}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              cancelConfirmText.toLowerCase() === 'cancel' && !cancelLoading
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="cancel-confirm-btn"
          >
            {cancelLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              isFree ? 'Cancel Account' : 'Cancel Subscription'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const MySubscription = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');

  useEffect(() => {
    console.log('🔥 NEW MySubscription Component Loaded');
    console.log('👤 User:', user?.email);
    fetchLiveData();
  }, []);

  const fetchLiveData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      console.log('📡 Fetching from API...');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/my-subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Live data received:', data);
        setSubscriptionData(data);
      } else {
        console.error('❌ API Error:', response.status);
      }
    } catch (error) {
      console.error('❌ Fetch Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentTier = subscriptionData?.tier || 'free';
  const isFree = currentTier === 'free';

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleCancelSubscription = async () => {
    if (cancelConfirmText.toLowerCase() !== 'cancel') {
      return;
    }
    
    setCancelLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh subscription data
        await fetchLiveData();
        setShowCancelModal(false);
        setCancelConfirmText('');
      } else {
        const error = await response.json();
        console.error('Cancel error:', error);
        alert(error.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Cancel error:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mb-4" />
        <p className="text-gray-600 text-lg">Loading your subscription details...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Management</h1>
        <p className="text-gray-600">Manage your TribalRoot.AI membership and billing</p>
      </div>

      {/* Main Content - Different layouts for Free vs Paid */}
      {isFree ? (
        /* FREE USER LAYOUT */
        <div className="space-y-6">
          {/* Current Status */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border-2 border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Gift className="h-8 w-8 text-gray-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Free Account</h2>
                </div>
                <p className="text-gray-600">Limited access to community features</p>
              </div>
              <div className="bg-white px-6 py-3 rounded-xl shadow-sm border border-gray-200">
                <p className="text-sm text-gray-500">Monthly Cost</p>
                <p className="text-3xl font-bold text-gray-900">$0</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-500 mb-1">Member Since</p>
                <p className="font-semibold text-gray-900">{formatDate(subscriptionData?.created_at || user?.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Account Status</p>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Active
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Access Level</p>
                <p className="font-semibold text-gray-900">Basic Features</p>
              </div>
            </div>
          </div>

          {/* Upgrade Promotion Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Seekers Path Card */}
            <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-8 text-white overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10">
                <Star className="h-10 w-10 mb-4" />
                <h3 className="text-2xl font-bold mb-2">The Seekers Path</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold">$9.99</span>
                  <span className="text-xl">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>AI Ancestry Analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Unlimited Crest Generator</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>The Guru AI Guide</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Healing & Herbal Remedies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Language Restoration Tool</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Treaty & Tribal Rights Education</span>
                  </li>
                </ul>
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-white text-blue-600 font-bold py-4 px-6 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center gap-2 shadow-lg"
                >
                  Upgrade Now
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Crown Card */}
            <div className="relative bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-8 text-white overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10">
                <Crown className="h-10 w-10 mb-4" />
                <h3 className="text-2xl font-bold mb-2">The Crown</h3>
                <div className="mb-2">
                  <span className="text-3xl font-bold">$129.99</span>
                  <span className="text-lg"> initial</span>
                </div>
                <div className="mb-6">
                  <span className="text-xl">then </span>
                  <span className="text-3xl font-bold">$9.99</span>
                  <span className="text-xl">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Everything in Seekers Path</span>
                  </li>
                  <li className="flex items-start">
                    <Crown className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Official Genealogy Paperwork</span>
                  </li>
                  <li className="flex items-start">
                    <Crown className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Private ID Application</span>
                  </li>
                  <li className="flex items-start">
                    <Crown className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">Priority Support</span>
                  </li>
                </ul>
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-white text-amber-600 font-bold py-4 px-6 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center gap-2 shadow-lg"
                >
                  Upgrade to Crown
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Feature Comparison */}
          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">What You&apos;re Missing</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Lock className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">AI Ancestry Analysis</h4>
                  <p className="text-sm text-gray-600">Discover your tribal roots with AI-powered genealogy research</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Lock className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Crest Generator</h4>
                  <p className="text-sm text-gray-600">Create unique family crests with traditional symbols</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Lock className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">The Guru (AI Guide)</h4>
                  <p className="text-sm text-gray-600">Chat with AI elder guide for tribal law and rights</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Lock className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Healing Wisdom</h4>
                  <p className="text-sm text-gray-600">Access natural remedies and traditional healing practices</p>
                </div>
              </div>
            </div>
          </div>

          {/* Cancel Subscription Section - For Free Users */}
          {subscriptionData?.status !== 'cancelled' && (
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Need to cancel your account?</p>
                  <p className="text-xs text-gray-400 mt-1">You can cancel your membership at any time</p>
                </div>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all text-sm font-medium"
                  data-testid="cancel-subscription-btn-free"
                >
                  <XCircle className="h-4 w-4" />
                  Cancel Subscription
                </button>
              </div>
            </div>
          )}

          {/* Cancelled Status Banner - For Free Users */}
          {subscriptionData?.status === 'cancelled' && (
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-800">Account Cancelled</p>
                    <p className="text-sm text-red-600">
                      Your account has been cancelled
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/subscription')}
                  className="mt-4 w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold"
                  data-testid="reactivate-btn-free"
                >
                  Reactivate Account
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* PAID USER LAYOUT */
        <div className="space-y-6">
          {/* Current Subscription Header */}
          <div className={`bg-gradient-to-br ${
            currentTier === 'seekers' 
              ? 'from-blue-500 to-purple-600' 
              : 'from-amber-500 to-orange-600'
          } rounded-2xl p-8 text-white`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  {currentTier === 'seekers' ? (
                    <Star className="h-10 w-10" />
                  ) : (
                    <Crown className="h-10 w-10" />
                  )}
                  <h2 className="text-3xl font-bold">
                    {currentTier === 'seekers' ? 'The Seekers Path' : 'The Crown'}
                  </h2>
                </div>
                <p className="text-white/90">
                  {subscriptionData?.status === 'cancelled' 
                    ? 'Cancelled - Access ends ' + formatDate(subscriptionData?.current_period_end)
                    : 'Active Premium Membership'
                  }
                </p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-8 py-4 rounded-xl">
                <p className="text-white/80 text-sm mb-1">Monthly</p>
                <p className="text-4xl font-bold">$9.99</p>
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <Calendar className="h-8 w-8 text-indigo-600 mb-3" />
              <p className="text-sm text-gray-500 mb-1">Member Since</p>
              <p className="text-lg font-bold text-gray-900">{formatDate(subscriptionData?.created_at)}</p>
            </div>
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <CreditCard className="h-8 w-8 text-green-600 mb-3" />
              <p className="text-sm text-gray-500 mb-1">Next Billing Date</p>
              <p className="text-lg font-bold text-gray-900">{formatDate(subscriptionData?.current_period_end)}</p>
            </div>
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <TrendingUp className="h-8 w-8 text-purple-600 mb-3" />
              <p className="text-sm text-gray-500 mb-1">Status</p>
              {subscriptionData?.status === 'cancelled' ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  <XCircle className="h-4 w-4 mr-1" />
                  Cancelled
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Active
                </span>
              )}
            </div>
          </div>

          {/* Management Actions */}
          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Manage Your Subscription</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <button
                onClick={async () => {
                  const token = localStorage.getItem('auth_token');
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/create-customer-portal-session`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ return_url: window.location.href })
                  });
                  if (res.ok) {
                    const data = await res.json();
                    window.location.href = data.url;
                  }
                }}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-semibold"
                data-testid="manage-billing-btn"
              >
                <CreditCard className="h-5 w-5" />
                Manage Billing & Payment
              </button>
              <button
                onClick={() => navigate('/subscription')}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 transition-all font-semibold"
                data-testid="view-plans-btn"
              >
                {currentTier === 'seekers' ? (
                  <>
                    <Crown className="h-5 w-5" />
                    Upgrade to The Crown
                  </>
                ) : (
                  <>
                    <Star className="h-5 w-5" />
                    View All Plans
                  </>
                )}
              </button>
            </div>

            {/* Cancel Subscription Section */}
            {subscriptionData?.status !== 'cancelled' && (
              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Need to cancel?</p>
                    <p className="text-xs text-gray-400 mt-1">Your access will continue until the end of your billing period</p>
                  </div>
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all text-sm font-medium"
                    data-testid="cancel-subscription-btn"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel Subscription
                  </button>
                </div>
              </div>
            )}

            {/* Cancelled Status Banner */}
            {subscriptionData?.status === 'cancelled' && (
              <div className="pt-6 border-t border-gray-200">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <div>
                      <p className="font-semibold text-red-800">Subscription Cancelled</p>
                      <p className="text-sm text-red-600">
                        Your access will end on {formatDate(subscriptionData?.current_period_end)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/subscription')}
                    className="mt-4 w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold"
                    data-testid="reactivate-btn"
                  >
                    Reactivate Subscription
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Features Unlocked */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">✨ Your Premium Features</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <span className="font-medium text-gray-900">AI Ancestry Analysis</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <span className="font-medium text-gray-900">Unlimited Crest Generator</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <span className="font-medium text-gray-900">The Guru AI Guide</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <span className="font-medium text-gray-900">Healing & Remedies</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <span className="font-medium text-gray-900">Language Restoration</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <span className="font-medium text-gray-900">Treaty Education</span>
              </div>
              {currentTier === 'crown' && (
                <>
                  <div className="flex items-center gap-3">
                    <Crown className="h-6 w-6 text-amber-600" />
                    <span className="font-medium text-gray-900">Official Genealogy Paperwork</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Crown className="h-6 w-6 text-amber-600" />
                    <span className="font-medium text-gray-900">Private ID Application</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <CancellationModal
          currentTier={currentTier}
          periodEnd={subscriptionData?.current_period_end}
          cancelConfirmText={cancelConfirmText}
          setCancelConfirmText={setCancelConfirmText}
          cancelLoading={cancelLoading}
          onCancel={handleCancelSubscription}
          onClose={() => {
            setShowCancelModal(false);
            setCancelConfirmText('');
          }}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

export default MySubscription;
